import {Grid, Typography} from '@material-ui/core';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import React, {useEffect, useState} from 'react';
import AthenaAPI from '../../../APIs/AthenaAPI';
import {formatCurrency} from '../../../Core/helpers/formatCurrency';
import RootStore from '../../../Core/mobx/RootStore';
import {useCommonModalStyles} from './common/modalStyles';
import {utcToZonedTime} from 'date-fns-tz';
import {IS_PRODUCTION} from '../../../constants';
import {AgenuimContextType} from '../../../Core/agenium/ageniumContext';
import {format} from 'date-fns';
import {Section} from '@agenium-ai/agenium-client-sdk/dist/models/section';
import FixedNavigation from './common/FixedNavigation';
import {CalcFocusData} from '../InstantAppModal';

interface AllDoneProps {
    calcFocusDataLocal?: CalcFocusData;
    onBackToClientsClicked: () => void;
    ageniumContext: AgenuimContextType;
}

export const AllDone: React.FC<AllDoneProps> = ({calcFocusDataLocal, onBackToClientsClicked, ageniumContext}) => {
    const {parameterStore} = RootStore.agentPortalStore;

    const commonClasses = useCommonModalStyles();
    const [initLoadAppData, setInitLoadAppData] = useState(false);
    const [banking4digits, setBanking4digits] = useState<string | null>(null);
    const [policyNumber, setPolicyNumber] = useState('');
    const [printValues, setPrintValues] = useState({
        beginsAt: '',
        coveredFor: '',
        firstMonthlyPayment: '',
        withdrawnOn: '',
    });

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (ageniumContext.applicationNumber) {
            const subscription = ageniumContext?.sdk?.getSections().subscribe({
                next: (s: Section[] | undefined) => {
                    let continueSearch = 1;
                    const lookForKey = (questions: any) => {
                        for (let i = 0; continueSearch && questions && i < questions.length; i++) {
                            if (questions[i]?.key === 'Case_Bank_AccountNumber') {
                                setBanking4digits(questions[i]?.value?.length > 4 ? questions[i].value.slice(-4) : '');
                                continueSearch--;
                            }
                            lookForKey(questions[i].questions);
                        }
                    };
                    for (let t = 0; s && t < s.length; t++) lookForKey(s[t].questions);
                },
            });
            return () => subscription?.unsubscribe();
        }
    }, [ageniumContext.applicationNumber]);

    useEffect(() => {
        const isMidnightUTC = (date: Date) => {
            // Check if all UTC components (hours, minutes, seconds, milliseconds) are zero
            return (
                date.getUTCHours() === 0 &&
                date.getUTCMinutes() === 0 &&
                date.getUTCSeconds() === 0 &&
                date.getUTCMilliseconds() === 0
            );
        };

        const getEffectiveDate = (reqEffectiveDate: Date) => {
            let effectiveDate = isMidnightUTC(reqEffectiveDate)
                ? utcToZonedTime(reqEffectiveDate, 'UTC')
                : reqEffectiveDate;
            if (effectiveDate.getDate() > 28) {
                effectiveDate.setMonth(effectiveDate.getMonth() + 1);
                effectiveDate.setDate(1);
            }
            return effectiveDate;
        };

        const getApplication = async () => {
            setIsLoading(true);
            let res;
            if (parameterStore.instantIulApplicationId) {
                res = await AthenaAPI.getApplicationById(parameterStore.instantIulApplicationId);
                const policySummary = res?.policyInfo?.policySummary?.[0];
                if (!policySummary) {
                    return {error: 'There was an issue fetching the policy'};
                }
                const {scenarioSummary = []} = policySummary;
                const [firstScenario = {}] = scenarioSummary;
                const {endOfYearValues = []} = firstScenario;
                const [secondYear = {}] = endOfYearValues;

                const initialMonthlyPremium = secondYear.sumPremiumsPaid / 12;
                const faceAmount = policySummary.coverageSummary[0]?.faceAmount;
                const effectiveDate = getEffectiveDate(new Date(policySummary.effectiveDate));

                setPrintValues({
                    beginsAt: format(effectiveDate, 'MMMM dd, yyyy'),
                    coveredFor: formatCurrency(faceAmount, true),
                    firstMonthlyPayment: formatCurrency(initialMonthlyPremium, true),
                    withdrawnOn: format(
                        new Date(
                            effectiveDate.getDate() > 28
                                ? new Date(effectiveDate).setMonth(effectiveDate.getMonth() + 1)
                                : new Date(effectiveDate).setDate(28),
                        ),
                        'MMMM dd, yyyy',
                    ),
                });
            }
            setIsLoading(false);
        };

        const useSavedData = () => {
            if (!calcFocusDataLocal?.out) {
                getApplication();
            } else {
                setIsLoading(true);
                const effectiveDate = getEffectiveDate(parameterStore.getQuote()?.prosperEffectiveDate.value);
                setPrintValues({
                    beginsAt: format(effectiveDate, 'MMMM dd, yyyy'),
                    coveredFor: formatCurrency(
                        (calcFocusDataLocal?.out?.find((t: {fieldName?: string}) => t?.fieldName === 'faceAmount')
                            ?.value as number) ?? 0,
                        true,
                    ),
                    firstMonthlyPayment: formatCurrency(
                        (calcFocusDataLocal?.out?.find((t: {fieldName?: string}) => t?.fieldName === 'monthlyPremium')
                            ?.value as number) ?? 0,
                        true,
                    ),
                    withdrawnOn: format(
                        new Date(
                            effectiveDate.getDate() > 28
                                ? new Date(effectiveDate).setMonth(effectiveDate.getMonth() + 1)
                                : new Date(effectiveDate).setDate(28),
                        ),
                        'MMMM dd, yyyy',
                    ),
                });
                setIsLoading(false);
            }
        };

        // If we have an instantIulApplicationId, attempt to get policy info from Calcfocus API
        // If not available, use data we saved, either locally or in S3
        // In case of error, default to saved data
        if (parameterStore.instantIulApplicationId) {
            setIsLoading(true);
            AthenaAPI.getPolicyByInstantIulApplicationId(parameterStore.instantIulApplicationId)
                .then((policy) => {
                    const baseCoverage = (policy.coverage as any[]).find((c) => c.baseIndicator == 'BASE');
                    if (policy) {
                        // Despite timestamp we send, calcfocus sets effective date to chosen date in UTC
                        const effectiveDate = format(
                            utcToZonedTime(new Date(policy.effectiveDate), 'UTC'),
                            'MMMM d, yyyy',
                        );
                        if (policy.policyNumber) setPolicyNumber(policy.policyNumber);
                        const issueDate = policy.issueDate
                            ? format(utcToZonedTime(new Date(policy.issueDate), 'UTC'), 'MMMM d, yyyy')
                            : effectiveDate;
                        setPrintValues({
                            beginsAt: effectiveDate,
                            coveredFor: formatCurrency(baseCoverage.faceAmount, true),
                            firstMonthlyPayment: formatCurrency(policy.modalPremium, true),
                            withdrawnOn: issueDate,
                        });
                        setIsLoading(false);
                    } else {
                        useSavedData();
                    }
                })
                .catch(() => {
                    useSavedData();
                });
        } else {
            useSavedData();
        }
    }, [calcFocusDataLocal, parameterStore.instantIulApplicationId]);

    useEffect(() => {
        if (!initLoadAppData && ageniumContext.applicationNumber && !ageniumContext.applicationData) {
            setInitLoadAppData(true);
            ageniumContext.loadApplicationData();
        }
    }, [initLoadAppData, ageniumContext.applicationNumber, ageniumContext.applicationData]);

    useEffect(() => {
        ageniumContext.initSdk();
    }, []);

    const emptyValuePlaceholder = (
        <span style={{background: '#efefef', paddingLeft: '16px', borderBottom: '1px solid #888'}}>&nbsp;</span>
    );

    return (
        <Grid container className={commonClasses.wrapper}>
            <Grid item container direction="column" justify="center">
                <Grid item>
                    <Typography variant="h4" className={commonClasses.title}>
                        All Done
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography style={{fontFamily: 'Lora', fontSize: '24px', fontWeight: 700, lineHeight: '29px'}}>
                        Additional Details
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography
                        style={{
                            fontFamily: 'FredokaRegular',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '26px',
                            color: '#838383',
                        }}
                    >
                        Details about your client’s new policy.
                    </Typography>
                </Grid>
                {ageniumContext.applicationData && !isLoading && banking4digits != null ? (
                    <Grid item>
                        <List style={{listStyleType: 'disc', paddingLeft: '16px'}}>
                            {[
                                <>
                                    Your coverage of <b>{printValues.coveredFor || emptyValuePlaceholder}</b> under the
                                    Indexed Universal Life Policy begins at <b>midnight</b> on{' '}
                                    <b>{printValues.beginsAt || emptyValuePlaceholder}</b>.
                                </>,
                                'This policy never expires as long as contributions are made, and it will build cash value over time.',
                                <>
                                    Your first monthly contribution of <b>{printValues.firstMonthlyPayment}</b> will be
                                    withdrawn from your bank account
                                    {banking4digits && (
                                        <>
                                            {' '}
                                            ending in <b>{banking4digits}</b>
                                        </>
                                    )}{' '}
                                    on <b>{printValues.withdrawnOn || emptyValuePlaceholder}</b>.
                                </>,
                                <>
                                    The <b>charge</b> will appear on your bank statement as <b>AMPLIFY-EMCLIFE</b>
                                </>,
                                <>
                                    Your policy number is{' '}
                                    <b>{ageniumContext.policyNumber || policyNumber || emptyValuePlaceholder}</b>
                                </>,
                                <>
                                    And just a heads-up: if the first payment doesn't go through due to insufficient
                                    funds, your bank may charge you a fee.
                                    <ListItem
                                        style={{
                                            display: 'list-item',
                                            color: '#456d69',
                                            padding: 0,
                                            paddingLeft: 0,
                                            marginLeft: '20px',
                                        }}
                                    >
                                        <p style={{fontFamily: 'FredokaRegular', fontSize: '16px', fontWeight: 500}}>
                                            Somebody from Amplify's Customer Success Team will reach out to help you set
                                            up another redraft attempt if this does happen.
                                        </p>
                                    </ListItem>
                                </>,
                            ].map((s, i) => (
                                <ListItem key={i} style={{display: 'list-item', color: '#456d69', padding: 0}}>
                                    <p style={{fontFamily: 'FredokaRegular', fontSize: '16px', fontWeight: 500}}>{s}</p>
                                </ListItem>
                            ))}
                        </List>
                    </Grid>
                ) : (
                    <>Loading...</>
                )}
            </Grid>
            <FixedNavigation isLoading={false} onNext={onBackToClientsClicked} onNextCustomText={'Back To Clients'} />
            {!IS_PRODUCTION && (
                <div style={{fontSize: '10px', marginTop: 50}}>
                    <div>calcFocusDataLocal</div>
                    <div style={{color: '#808'}}>{JSON.stringify(calcFocusDataLocal, null, 2)}</div>
                </div>
            )}
        </Grid>
    );
};
